/**
 * The asset page's Masters tab (1.2.0): every master of the current version, the primary first. Each shows its variant
 * (editorial, from Administration → Metadata model → Master variants) and its format (from the probe, never typed),
 * file, size, where it is stored and whether it is available. Actions, each with its reason when it isn't offered:
 *   Add master…   a resumable upload into this version, with a Variant (default Programme); the format is read from
 *                 the file. A file that is not the same content is refused (DIFFERENT_CONTENT), and a second master of
 *                 the same variant and format asks to replace the existing one (kept until the new one is verified).
 *   Edit variant  audited; refused when the asset already has that variant in that format (MASTER_EXISTS).
 *   Remove        a further master and its copies; never the primary (it goes with the asset, or a new version).
 * 1.2.2: masters belong to their version. After a new version, a notice names the variants the version before had
 * and this one has not ("Version 3 had Textless and M&E; add them for version 4"), with Add master….
 */
import { useRef, useState } from 'react';
import { AlertTriangle, Cloud, HardDrive, Layers, Loader2, Plus, Server, Trash2, Upload } from 'lucide-react';
import { MASTER_CONFLICTS, PROGRAMME_VARIANT_KEY, andList, type AssetDetail, type MasterConflict, type MasterDTO, type MasterVariantDTO } from '@prism/contracts';
import { ApiError, errorMessage } from '../api/client';
import { useAddMaster, useAssetMasters, useMasterMutations, useMasterVariants, useMe } from '../api/hooks';
import { Availability } from '../components/Status';
import { Modal } from '../components/Modal';
import { toast } from '../components/Toasts';
import { uploader } from '../upload/uploader';
import { uploadAccept } from '../lib/upload-accept';
import { bytes, rate, relative } from '../lib/format';

const SOURCE_LABELS: Record<NonNullable<MasterDTO['source']>, string> = { upload: 'Uploaded', watch: 'Watch folder', workflow: 'Workflow', checkin: 'Checked in', api: 'API', export: 'Sequence export' };
export const CONFLICT_LABELS: Record<MasterConflict, string> = {
  refuse: 'Ask me (the upload tray offers Replace)',
  replace: 'Replace it (kept until the new one is verified)',
  skip: 'Keep the existing one (skip this file)',
};

/** The masters an asset page shows: the list route, else the asset's own copy (AssetDetail.masters). */
export function useMastersOf(asset: Pick<AssetDetail, 'id' | 'masters'>) {
  const q = useAssetMasters(asset.id);
  return { masters: q.data?.items ?? asset.masters ?? [], loading: q.isPending && !asset.masters, query: q };
}

/** Why a master cannot be removed, or null when it can. */
export function removeBlocked(m: Pick<MasterDTO, 'primary'>, count: number): string | null {
  if (!m.primary) return null;
  return count > 1
    ? 'The primary master stays while other masters exist. It is the version’s original: a new version replaces it, or deleting the asset removes it.'
    : 'This is the asset’s only master, its original. Delete the asset (or upload a new version) instead.';
}

export function MastersTab({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const { masters, loading, query } = useMastersOf(asset);
  const variants = useMasterVariants().data?.items ?? [];
  const mut = useMasterMutations(asset.id);
  const addMaster = useAddMaster(asset.id);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const canAdd = me.permissions.includes('asset.upload') && !asset.deleted && asset.status !== 'placeholder';
  const canEdit = me.permissions.includes('asset.edit_metadata') && !asset.deleted;
  const canRemove = me.permissions.includes('asset.delete') && !asset.deleted;
  const previous = query.data?.previous ?? null;

  if (asset.status === 'placeholder') return <p className="muted" data-testid="masters-tab">No media yet, so this asset has no masters.</p>;

  const setVariant = async (m: MasterDTO, variantId: string) => {
    setEditing(null);
    if (variantId === m.variant.id) return;
    try {
      const next = await mut.setVariant.mutateAsync({ masterId: m.id, variantId });
      toast(`Now ${next.label}`);
    } catch (err) {
      toast((err as ApiError).code === 'MASTER_EXISTS' ? `${errorMessage(err)}. Remove or change that master first.` : errorMessage(err), 'error');
    }
  };
  const remove = async (m: MasterDTO) => {
    if (!window.confirm(`Remove the master “${m.label}”? Its file is deleted from every storage location. The other masters stay.`)) return;
    try {
      await mut.remove.mutateAsync(m.id);
      toast(`Removed ${m.label}`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  };

  return (
    <div data-testid="masters-tab">
      <div className="masters-head">
        <p className="muted" style={{ margin: 0, flex: 1 }}>
          The masters of version {asset.versionNumber}: the same content in other resolutions or variants. Export, Send to edit and QC choose between them; the
          player and the sequence editor use the primary’s proxy.
        </p>
        {canAdd
          ? <button className="btn sm" onClick={() => setAdding(true)} data-testid="add-master"><Plus size={13} /> Add master…</button>
          : !asset.deleted && <span className="faint" style={{ fontSize: 12 }} title="Adding a master needs the upload permission">Your role cannot add masters</span>}
      </div>
      {previous && !asset.deleted && (
        <div className="banner warning" data-testid="masters-previous" style={{ marginBottom: 10 }}><div className="banner-body">
          <span><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Version {previous.versionNumber} had {andList(previous.missing.map((m) => m.name))}; add {previous.missing.length === 1 ? 'it' : 'them'} for version {previous.currentNumber}.
            {' '}<span className="faint">They stay with version {previous.versionNumber}, which they match.</span></span>
          {canAdd && <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAdding(true)} data-testid="masters-previous-add"><Plus size={13} /> Add master…</button>}
        </div></div>
      )}
      {query.isError && !asset.masters && (query.error as ApiError).status !== 404 && <p className="faint" style={{ fontSize: 12 }}>The masters could not be loaded: {errorMessage(query.error)}</p>}
      {loading ? <div className="skeleton" style={{ height: 120 }} /> : (
        <div className="masters-list" data-testid="masters-list">
          {masters.map((m) => {
            const blocked = removeBlocked(m, masters.length);
            return (
              <div className="master-row" key={m.id} data-testid="master-row" data-primary={m.primary || undefined} data-master-id={m.id}>
                <div className="master-main">
                  <div className="master-title">
                    <Layers size={14} aria-hidden />
                    {editing === m.id ? (
                      <select className="input sm" autoFocus defaultValue={m.variant.id} aria-label={`Variant of ${m.label}`} data-testid="master-variant-select"
                        onChange={(e) => void setVariant(m, e.target.value)} onBlur={() => setEditing(null)}>
                        {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    ) : <strong data-testid="master-variant">{m.variant.name}</strong>}
                    <span className="pill mono">{m.variant.code}</span>
                    {m.primary && <span className="pill accent" title="The version’s original">Primary</span>}
                    <span className="master-format" data-testid="master-format" title={m.format.label}>{m.format.label}</span>
                  </div>
                  <div className="master-sub">
                    <span title={m.filename ?? undefined}>{m.filename ?? 'No file name'}</span>
                    <span>{bytes(m.sizeBytes)}</span>
                    {m.startTimecode && <span className="mono" title="Start timecode">TC {m.startTimecode}</span>}
                    {m.source && <span>{SOURCE_LABELS[m.source]}{m.createdBy ? ` by ${m.createdBy}` : ''} {relative(m.createdAt)}</span>}
                  </div>
                  {!m.sharedTimeline && (
                    <div className="master-note" data-testid="master-own-timeline">
                      <AlertTriangle size={12} aria-hidden /> {m.variant.name} may differ in length from the primary: it keeps its own timecode, and the asset’s subtitles and markers don’t apply to it.
                    </div>
                  )}
                  {canRemove && blocked && masters.length > 1 && <div className="master-note faint" data-testid="master-remove-reason">{blocked}</div>}
                  <div className="master-locations">
                    {m.locations.length ? m.locations.map((l) => (
                      <span key={l.storageLocationId} className="master-location" title={l.key}>
                        {l.storageKind === 's3' || l.storageKind === 'oci' ? <Cloud size={12} /> : l.storageKind === 'agent' ? <Server size={12} /> : <HardDrive size={12} />}
                        {l.storageName}{l.status === 'missing' ? ' (missing)' : l.archive?.state === 'archived' ? ' (archived)' : ''}
                      </span>
                    )) : <span className="faint">Not stored yet</span>}
                    <Availability value={m.availability} />
                  </div>
                </div>
                <div className="master-actions">
                  {canEdit && editing !== m.id && (
                    <button className="btn ghost sm" onClick={() => setEditing(m.id)} disabled={mut.setVariant.isPending || !variants.length} data-testid="edit-variant">Edit variant</button>
                  )}
                  {canRemove && (
                    <button className="btn ghost icon sm danger" aria-label={`Remove ${m.label}`} data-testid="remove-master" disabled={Boolean(blocked) || mut.remove.isPending}
                      title={blocked ?? 'Remove this master and its copies'} onClick={() => void remove(m)}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            );
          })}
          {!masters.length && <p className="muted" data-testid="masters-empty">No masters are recorded for this version yet. The primary appears here once the original is processed.</p>}
        </div>
      )}
      {addMaster.inFlight.length > 0 && (
        <div className="master-uploads" data-testid="master-uploads">
          <p className="section-label">Adding</p>
          {addMaster.inFlight.map((u) => (
            <div className="master-upload" key={u.localId} data-state={u.state}>
              {u.state === 'error' || u.state === 'master_exists' ? <AlertTriangle size={13} color="var(--warning)" /> : <Loader2 size={13} className="spin" />}
              <span className="name">{u.file.name} · {u.master!.variantName}</span>
              <span className="muted">
                {u.state === 'uploading' ? `${bytes(u.sent)} of ${bytes(u.total)}${u.rate ? ` · ${rate(u.rate)}` : ''}`
                  : u.state === 'finalizing' ? 'Checking it is the same content…'
                  : u.state === 'master_exists' || u.state === 'error' ? u.error : u.state}
              </span>
              {u.state === 'master_exists' && <button className="btn sm" onClick={() => uploader.replaceMaster(u.localId)}>Replace it</button>}
              {u.state !== 'finalizing' && <button className="btn ghost sm" onClick={() => void uploader.cancel(u.localId)}>{u.state === 'error' ? 'Dismiss' : 'Cancel'}</button>}
            </div>
          ))}
        </div>
      )}
      {adding && <AddMasterDialog asset={asset} variants={variants} onClose={() => setAdding(false)} onAdd={(file, v, onConflict) => {
        startAddMaster(addMaster.add, asset, file, v, onConflict);
        setAdding(false);
      }} />}
    </div>
  );
}

/** The Variant (default Programme) and "If the asset has one" choices of an "Add master…" upload. */
export function useMasterChoice(variants: MasterVariantDTO[]) {
  const programme = variants.find((v) => v.key === PROGRAMME_VARIANT_KEY) ?? variants[0];
  const [variantId, setVariantId] = useState<string | undefined>(programme?.id);
  const [onConflict, setOnConflict] = useState<MasterConflict>('refuse');
  const variant = variants.find((v) => v.id === (variantId ?? programme?.id));
  return { variant, setVariantId, onConflict, setOnConflict };
}

/** Starts an "Add master…" upload (the masters hook's add, a toast); shared by the Masters tab and Upload… (1.2.2). */
export function startAddMaster(add: ReturnType<typeof useAddMaster>['add'], asset: AssetDetail, file: File, v: MasterVariantDTO, onConflict: MasterConflict) {
  add(file, { assetCode: asset.assetCode, title: asset.title, variantId: v.key === PROGRAMME_VARIANT_KEY ? null : v.id, variantName: v.name, onConflict });
  toast(`Uploading ${file.name} as a ${v.name} master`);
}

/** A file chooser line: Choose file… and the chosen name and size. */
export function FileChooser({ file, onFile, testId }: { file: File | null; onFile: (f: File | null) => void; testId: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="field">
      <label>File</label>
      <input ref={input} type="file" hidden accept={uploadAccept()} data-testid={`${testId}-file`} onChange={(e) => { onFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
        <button className="btn sm" onClick={() => input.current?.click()}><Upload size={13} /> Choose file…</button>
        <span className={file ? 'break-any' : 'faint'} data-testid={`${testId}-filename`}>{file ? `${file.name} · ${bytes(file.size)}` : 'No file chosen'}</span>
      </div>
    </div>
  );
}

/** The Variant and conflict fields of "Add master…". */
export function MasterChoiceFields({ variants, choice }: { variants: MasterVariantDTO[]; choice: ReturnType<typeof useMasterChoice> }) {
  const { variant, setVariantId, onConflict, setOnConflict } = choice;
  return (
    <>
      <div className="field">
        <label htmlFor="add-master-variant">Variant</label>
        <select id="add-master-variant" className="input" value={variant?.id ?? ''} onChange={(e) => setVariantId(e.target.value)} data-testid="add-master-variant">
          {variants.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.code})</option>)}
        </select>
        <p className="faint" style={{ fontSize: 12, margin: '4px 0 0' }} data-testid="add-master-rule">
          {variant?.sameLengthRequired === false
            ? `${variant.name} may be longer or shorter than the primary (its frame rate must still fit). It keeps its own timecode; the subtitles and markers don’t apply to it.`
            : `It must be as long as the primary master to within a frame, at a compatible frame rate; otherwise it is refused as different content.`}
        </p>
      </div>
      <div className="field">
        <label htmlFor="add-master-conflict">If the asset has a {variant?.name ?? ''} master in the same format</label>
        <select id="add-master-conflict" className="input" value={onConflict} onChange={(e) => setOnConflict(e.target.value as MasterConflict)} data-testid="add-master-conflict">
          {MASTER_CONFLICTS.map((c) => <option key={c} value={c}>{CONFLICT_LABELS[c]}</option>)}
        </select>
      </div>
    </>
  );
}

function AddMasterDialog({ asset, variants, onClose, onAdd }: {
  asset: AssetDetail; variants: MasterVariantDTO[]; onClose: () => void; onAdd: (file: File, variant: MasterVariantDTO, onConflict: MasterConflict) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const choice = useMasterChoice(variants);
  const { variant, onConflict } = choice;
  return (
    <Modal title={`Add a master to ${asset.assetCode}`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!file || !variant} onClick={() => file && variant && onAdd(file, variant, onConflict)} data-testid="add-master-start"><Upload size={14} /> Upload</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>
        A further master of version {asset.versionNumber}: the same content in another resolution or variant. Its format is read from the file after the upload.
      </p>
      <FileChooser file={file} onFile={setFile} testId="add-master" />
      <MasterChoiceFields variants={variants} choice={choice} />
    </Modal>
  );
}
